<script setup lang="ts">
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@forgd/ui-kit/dialog'
import { XMarkIcon } from '@heroicons/vue/24/outline'

const { isOpen, view, variant, mandatory, setView, startRegister, openOAuthFinalize, close, closeAfterSuccess } = useLoginModal()
const auth = useAuth()
const route = useRoute()
const supabase = useSupabaseClient()

const isRegisterDetails = computed(() =>
  view.value === 'register-details' || view.value === 'register-finalizing',
)

function onOpenChange(open: boolean) {
  if (!open) {
    // A mandatory (OAuth finalize) modal can't be dismissed — the user must finalize
    // registration before they can continue.
    if (mandatory.value) {
      return
    }
    close()
  }
}

function onDismissAttempt(event: Event) {
  if (mandatory.value) {
    event.preventDefault()
  }
}

watch(() => auth.isLoggedIn, (loggedIn) => {
  if (loggedIn && isOpen.value) {
    closeAfterSuccess()
  }
})

function nameFromMetadata(meta: Record<string, any>) {
  const firstName = meta.given_name || meta.first_name || ''
  const lastName = meta.family_name || meta.last_name || ''
  if (firstName || lastName) {
    return { firstName, lastName }
  }
  const full = String(meta.name || meta.full_name || '').trim()
  if (!full) {
    return { firstName: '', lastName: '' }
  }
  const [first, ...rest] = full.split(/\s+/)
  return { firstName: first, lastName: rest.join(' ') }
}

async function maybeOpenOAuthFinalize() {
  if (route.query.auth !== 'finalize') {
    return
  }
  await auth.waitForAuth({ from: 'auth/finalize-modal' })
  if (!auth.isLoggedIn || auth.me?.isOnboarded) {
    return
  }
  const { data: { user } } = await supabase.auth.getUser()
  openOAuthFinalize(nameFromMetadata(user?.user_metadata ?? {}))
}

onMounted(maybeOpenOAuthFinalize)
</script>

<template>
  <Dialog :open="isOpen" @update:open="onOpenChange">
    <DialogContent
      class="max-w-[1025px] w-full gap-0 overflow-hidden rounded-lg p-0"
      :hide-close="true"
      @escape-key-down="onDismissAttempt"
      @pointer-down-outside="onDismissAttempt"
      @interact-outside="onDismissAttempt"
    >
      <DialogTitle class="sr-only">
        {{ variant === 'continue' ? 'Ready to continue?' : 'Log in to Forgd' }}
      </DialogTitle>
      <DialogDescription class="sr-only">
        {{ variant === 'continue'
          ? 'Login or register to Forgd, and continue with your progress.'
          : 'Sign in to your Forgd account with Google or your email and password.' }}
      </DialogDescription>

      <button
        v-if="!mandatory"
        type="button"
        class="absolute right-1 top-1 z-10 flex size-11 items-center justify-center rounded-md text-secondary transition-colors hover:text-primal md:hidden"
        aria-label="Close"
        @click="close"
      >
        <XMarkIcon class="size-5" />
      </button>

      <div
        class="grid max-h-[94dvh] md:grid-cols-[500fr_525fr]"
        :class="{
          'md:h-[632px]': view === 'advisor-form' || view === 'advisor-success' || view === 'forgot-password',
          'md:h-[538px]': view === 'register-details' || view === 'register-finalizing',
        }"
      >
        <div class="min-h-0 overflow-y-auto p-5 font-display">
          <AuthLoginForm
            v-if="view === 'login'"
            :variant="variant"
            @success="closeAfterSuccess"
            @advisor="setView('advisor-form')"
            @register="startRegister"
            @forgot="setView('forgot-password')"
          />
          <AuthForgotPasswordForm
            v-else-if="view === 'forgot-password'"
            @back="setView('login')"
            @login="setView('login')"
            @register="startRegister"
          />
          <AuthAdvisorForm
            v-else-if="view === 'advisor-form'"
            @back="setView('login')"
            @success="setView('advisor-success')"
          />
          <AuthAdvisorSuccess v-else-if="view === 'advisor-success'" />
          <AuthRegisterForm v-else-if="view === 'register'" />
          <AuthRegisterDetails v-else-if="view === 'register-details'" />
          <AuthRegisterCheckEmail v-else-if="view === 'register-finalizing'" />
        </div>

        <div class="hidden p-4 md:block">
          <AuthAdvisorPanel
            v-if="view.startsWith('advisor')"
            dismissible
            class="h-full"
            @close="close"
          />
          <AuthRegisterPanel
            v-else-if="isRegisterDetails"
            :dismissible="!mandatory"
            class="h-full"
            @close="close"
          />
          <AuthTestimonialPanel
            v-else
            dismissible
            class="h-full"
            @close="close"
          />
        </div>
      </div>
    </DialogContent>
  </Dialog>
</template>
